import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { api } from './client'
import { queryKeys } from '../lib/query'
import type { Lead, LeadComment, LeadStatus, LeadSource, LostReason, PaginatedResponse, SpecificationVersion } from '@1130-studio/shared'

// Types
interface LeadsFilters {
  status?: LeadStatus
  source?: LeadSource
  assignedTo?: string
  search?: string
  page?: number
  pageSize?: number
}

interface CreateLeadInput {
  name: string
  email?: string
  phone?: string
  telegram?: string
  message?: string
  source?: LeadSource
  preferredContact?: string[]
}

interface UpdateLeadInput {
  name?: string
  email?: string
  phone?: string
  telegram?: string
  message?: string
  status?: LeadStatus
  assignedTo?: string | null
  specification?: string
  preferredContact?: string[] | null
  lostReason?: LostReason | null
  lostComment?: string | null
}

// Get leads list
export function useLeads(filters: LeadsFilters = {}) {
  return useQuery({
    queryKey: queryKeys.leads.list(filters),
    queryFn: () => {
      const params = new URLSearchParams()
      if (filters.status) params.set('status', filters.status)
      if (filters.source) params.set('source', filters.source)
      if (filters.assignedTo) params.set('assignedTo', filters.assignedTo)
      if (filters.search) params.set('search', filters.search)
      if (filters.page) params.set('page', String(filters.page))
      if (filters.pageSize) params.set('pageSize', String(filters.pageSize))

      const queryString = params.toString()
      return api.get<PaginatedResponse<Lead>>(`/leads${queryString ? `?${queryString}` : ''}`)
    },
  })
}

// Get single lead
export function useLead(id: string) {
  return useQuery({
    queryKey: queryKeys.leads.detail(id),
    queryFn: () => api.get<Lead>(`/leads/${id}`),
    enabled: !!id,
  })
}

// Create lead
export function useCreateLead() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (data: CreateLeadInput) => api.post<Lead>('/leads', data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.leads.all })
    },
  })
}

// Update lead
export function useUpdateLead() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: UpdateLeadInput }) =>
      api.patch<Lead>(`/leads/${id}`, data),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.leads.all })
      queryClient.invalidateQueries({ queryKey: queryKeys.leads.detail(variables.id) })
    },
  })
}

// Delete lead
export function useDeleteLead() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (id: string) => api.delete(`/leads/${id}`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.leads.all })
    },
  })
}

// Convert lead to client
export function useConvertLead() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (id: string) => api.post<{
      leadId: string
      name: string
      email?: string
      phone?: string
      telegram?: string
      specification?: string
    }>(`/leads/${id}/convert`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.leads.all })
    },
  })
}

// Get lead comments
export function useLeadComments(leadId: string) {
  return useQuery({
    queryKey: queryKeys.leads.comments(leadId),
    queryFn: () => api.get<LeadComment[]>(`/leads/${leadId}/comments`),
    enabled: !!leadId,
  })
}

// Create lead comment
export function useCreateLeadComment() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ leadId, content }: { leadId: string; content: string }) =>
      api.post<LeadComment>(`/leads/${leadId}/comments`, { content }),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.leads.comments(variables.leadId) })
    },
  })
}

// Get specification versions
export function useSpecificationVersions(leadId: string) {
  return useQuery({
    queryKey: queryKeys.leads.specifications(leadId),
    queryFn: () => api.get<SpecificationVersion[]>(`/leads/${leadId}/specifications`),
    enabled: !!leadId,
  })
}

// Create specification version
export function useCreateSpecificationVersion() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({
      leadId,
      content,
      changeSummary,
      isAiGenerated,
    }: {
      leadId: string
      content: string
      changeSummary?: string
      isAiGenerated?: boolean
    }) =>
      api.post<SpecificationVersion>(`/leads/${leadId}/specifications`, {
        content,
        changeSummary,
        isAiGenerated,
      }),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.leads.specifications(variables.leadId) })
      queryClient.invalidateQueries({ queryKey: queryKeys.leads.detail(variables.leadId) })
    },
  })
}
